import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { insertCustomerSchema } from '@shared/schema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { PlusCircle } from 'lucide-react';
import { z } from 'zod';
import { validateStaffPhone } from '@shared/phone-quality';

// Enhanced customer form schema with pipeline stage
const customerFormSchema = insertCustomerSchema
  .extend({
    pipelineStageId: z.number().min(1, 'Please select a pipeline stage'),
  })
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    userId: true,
    pipelineUpdatedAt: true,
    leadScore: true,
    lastContactDate: true,
    nextContactDate: true,
    initialContactDate: true,
    siteAssessmentDate: true,
    dealValue: true,
    externalProjectId: true,
    opensolarProjectId: true,
    electricitySupplier: true,
    currentTariff: true,
    electricityUsage: true,
    propertyType: true,
    roofType: true,
    yearBuilt: true,
    additionalData: true,
  });

type CustomerFormValues = z.infer<typeof customerFormSchema>;

interface CustomerModalProps {
  onCustomerCreated?: (customerId: number) => void;
  triggerText?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CustomerModal({
  onCustomerCreated,
  triggerText = 'Add New Customer',
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: CustomerModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { toast } = useToast();

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  // Get pipeline stages for the select dropdown
  const { data: pipelineStages } = useQuery({
    queryKey: ['/api/pipeline-stages'],
  });

  // Form setup with zod validation
  const form = useForm<CustomerFormValues>({
    // Phone stays optional here; only placeholders like 000 or tbc are refused.
    resolver: zodResolver(
      customerFormSchema.superRefine((values, ctx) => {
        const message = validateStaffPhone({ phone: values.phone, required: false });
        if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message });
      })
    ),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      postcode: '',
      source: 'website',
      status: 'active',
      notes: '',
      pipelineStageId: 1, // Default to first stage
      pipelineNotes: '',
    },
  });

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: async (data: CustomerFormValues) => {
      return await apiRequest('POST', '/api/customers', data);
    },
    onSuccess: (data) => {
      toast({
        title: 'Customer created',
        description: 'The customer has been successfully created.',
      });

      // Invalidate customers query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });

      // Call the callback if provided
      if (onCustomerCreated && data && data.id) {
        onCustomerCreated(data.id);
      }

      // Reset form and close modal
      form.reset();
      setOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error creating customer',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Form submission handler
  const onSubmit = (data: CustomerFormValues) => {
    createCustomerMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <PlusCircle className="h-4 w-4" />
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogDescription>
            Create a new customer. Fill in all required fields marked with an asterisk (*).
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Full name or company" />
                  </FormControl>
                  <FormDescription>The full name of the customer or company</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        type="email"
                        placeholder="email@example.com"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} placeholder="Phone number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="Customer's address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postcode</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} placeholder="Postcode/Zip code" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pipelineStageId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pipeline Stage *</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a pipeline stage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Array.isArray(pipelineStages) &&
                        pipelineStages?.map((stage: any) => (
                          <SelectItem key={stage.id} value={stage.id.toString()}>
                            {stage.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>Choose the appropriate stage for this customer</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="Additional notes" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createCustomerMutation.isPending}>
                {createCustomerMutation.isPending ? 'Creating...' : 'Create Customer'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
